<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="d-flex mb-6 align-center">
        <h2>{{ $t('memberDataSection.permissionsTitle') }}</h2>
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" class="mr-3" height="36" @click="goBack">{{ $t('back') }}</v-btn>
        <v-btn color="primary" dark height="36" :loading="saving" :disabled="!changedPerm.length" @click="savePermissions">
          {{ $t('permissions.savePermissions') }}
        </v-btn>
      </div>

      <v-card elevation="0" class="px-5 pt-4 pb-4 perm-card">
        <div class="d-flex flex-wrap align-end mb-4" style="gap: 16px">
          <div style="max-width: 300px; width: 100%">
            <label class="caption grey--text d-block mb-1" style="visibility: hidden">{{ $t('search') }}</label>
            <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" outlined dense hide-details
              :placeholder="$t('search')"></v-text-field>
          </div>
          <div style="min-width: 220px">
            <label class="caption grey--text d-block mb-1">{{ $t('roleRequest.userRole') }}</label>
            <v-select :value="roleId" :items="roles" item-text="name" item-value="id" outlined dense hide-details
              :loading="loading" @change="onRoleChange"></v-select>
          </div>
        </div>

        <v-data-table :headers="headers" :items="filteredModules" :loading="loading" hide-default-footer
          :items-per-page="-1" item-key="id" class="perm-table">
          <template #item.name="{ item }">{{ moduleDisplayName(item) }}</template>
          <template #item.view="{ item }">
            <v-simple-checkbox :ripple="false" color="primary" :value="isChecked(item, colMap.view)"
              :disabled="!permFor(item, colMap.view)"
              @input="(val) => togglePerm(item, colMap.view, val)"></v-simple-checkbox>
          </template>
          <template #item.create="{ item }">
            <v-simple-checkbox :ripple="false" color="primary" :value="isChecked(item, colMap.create)"
              :disabled="!permFor(item, colMap.create)"
              @input="(val) => togglePerm(item, colMap.create, val)"></v-simple-checkbox>
          </template>
          <template #item.update="{ item }">
            <v-simple-checkbox :ripple="false" color="primary" :value="isChecked(item, colMap.update)"
              :disabled="!permFor(item, colMap.update)"
              @input="(val) => togglePerm(item, colMap.update, val)"></v-simple-checkbox>
          </template>
          <template #item.delete="{ item }">
            <v-simple-checkbox :ripple="false" color="primary" :value="isChecked(item, colMap.delete)"
              :disabled="!permFor(item, colMap.delete)"
              @input="(val) => togglePerm(item, colMap.delete, val)"></v-simple-checkbox>
          </template>
          <template #item.all="{ item }">
            <v-simple-checkbox :key="`all-${item.id}`" :ripple="false" color="primary"
              :value="isAllChecked(item)"
              @input="(val) => toggleAll(item, val)"></v-simple-checkbox>
          </template>
          <template #no-data>
            <div class="text-center py-8 grey--text">{{ $t('permissions.noPermMsg') }}</div>
          </template>
        </v-data-table>

        <!-- Buying Station Access Scope -->
        <div class="mt-6">
          <div class="font-weight-bold mb-1">{{ isNaccuUser ? $t('memberDataSection.factoryAccessScopeTitle') : $t('memberDataSection.accessScopeTitle') }}</div>
          <div class="caption grey--text mb-2">{{ isNaccuUser ? $t('memberDataSection.factoryAccessScopeDesc') : $t('memberDataSection.accessScopeDesc') }}</div>
          <v-checkbox v-model="allowMultiple" hide-details color="primary" :loading="savingScope"
            :disabled="savingScope"
            :label="isNaccuUser ? $t('memberDataSection.factoryAccessScopeLabel') : $t('memberDataSection.accessScopeLabel')" @change="saveAccessScope"></v-checkbox>
        </div>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import _ from "lodash";
import loadingMixin from "@/mixins/LoadingMixin";
import getPermittedActions from "@/mixins/GetPermittedActions";
import { isKenyaClient } from "@/utils";
import PermissionsService from "@/_services/PermissionsService";
import BuyingStationService from "@/_services/BuyingStationService";

// Fixed column order matching the design; each maps the backend permission_name aliases.
const PERM_COLS = [
  { key: "view", names: ["view", "get", "read"] },
  { key: "create", names: ["create", "post"] },
  { key: "update", names: ["update", "put", "edit"] },
  { key: "delete", names: ["delete", "destroy"] },
];

// Fixed display order of the factory/buying-station modules in the permissions matrix.
const MODULE_ORDER = [
  "coffee/buying-station",            // Buying Station (shown as "Factory" for Kenya)
  "coffee/buying-station/purchase",   // Purchase Order
  "coffee/buying-station/processing", // Processing Batch
  "buying_station_parchment_delivery",// Parchment Delivery
  "buying_station_leftover_management",// Leftover Management
];

export default {
  name: "BuyingStationRolePermissions",
  mixins: [loadingMixin, getPermittedActions],
  // Remember whether we navigated in from another in-app page, so Back can pop history
  // (instead of pushing a new Roles entry, which stacks up and makes browser-Back loop).
  beforeRouteEnter(to, from, next) {
    next((vm) => { vm.cameFromApp = !!(from && from.name); });
  },
  data() {
    return {
      cameFromApp: false,
      roleId: this.$route.params.roleId,
      roles: [],
      roleName: "",
      membershipId: null,
      allowMultiple: false,
      appPermissions: [],
      changedPerm: [],
      search: "",
      loading: false,
      saving: false,
      savingScope: false,
      permCols: PERM_COLS,
    };
  },
  computed: {
    isNaccuUser() {
      return isKenyaClient();
    },
    breadcrumbs() {
      return [
        { text: this.$t("memberDataSection.factories"), disabled: false, to: { name: "CoffeeBuyingStation" } },
        { text: this.isNaccuUser ? this.$t("memberDataSection.factoryRoles") : this.$t("memberDataSection.buyingStationRoles"), disabled: false, to: { name: "CoffeeBuyingStationRoles" } },
        { text: this.$t("memberDataSection.permissionsTitle"), disabled: true },
      ];
    },
    headers() {
      return [
        { text: this.$t("permissions.moduleName"), value: "name", sortable: false },
        { text: this.$t("permissions.view"), value: "view", sortable: false, align: "center" },
        { text: this.$t("permissions.create"), value: "create", sortable: false, align: "center" },
        { text: this.$t("permissions.update"), value: "update", sortable: false, align: "center" },
        { text: this.$t("permissions.delete"), value: "delete", sortable: false, align: "center" },
        { text: this.$t("all"), value: "all", sortable: false, align: "center" },
      ];
    },
    colMap() {
      return PERM_COLS.reduce((acc, c) => { acc[c.key] = c; return acc; }, {});
    },
    // Flatten parent modules -> leaf modules for a single matrix table.
    allModules() {
      return (this.appPermissions || []).flatMap((p) => p.modules || []);
    },
    filteredModules() {
      // Apply the fixed display order (unknown modules fall to the end).
      const ordered = [...this.allModules].sort((a, b) => {
        const ia = MODULE_ORDER.indexOf(a.id);
        const ib = MODULE_ORDER.indexOf(b.id);
        return (ia === -1 ? MODULE_ORDER.length : ia) - (ib === -1 ? MODULE_ORDER.length : ib);
      });
      const term = (this.search || "").trim().toLowerCase();
      if (!term) return ordered;
      return ordered.filter((m) => (this.moduleDisplayName(m) || "").toLowerCase().includes(term));
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      this.loading = true;
      try {
        const rolesRes = await BuyingStationService.getStationRoles();
        this.roles = (rolesRes && rolesRes.data) || [];
        // Default to the routed role, else the first available.
        if (!this.roles.find((r) => r.id === this.roleId)) {
          this.roleId = this.roles.length ? this.roles[0].id : this.roleId;
        }
        await this.loadPermissions();
      } catch (e) {
        this.$notify({ text: (e && e.data && e.data.message) || "Failed to load permissions", type: "error" });
      } finally {
        this.loading = false;
      }
    },
    onRoleChange(roleId) {
      this.roleId = roleId;
      this.changedPerm = [];
      this.loadPermissions();
    },
    async loadPermissions() {
      const role = this.roles.find((r) => r.id === this.roleId);
      if (!role || !role.membershipId) {
        this.appPermissions = [];
        this.$notify({ text: "This role has no membership configured — permissions unavailable.", type: "error" });
        return;
      }
      this.loading = true;
      try {
        this.roleName = role.name;
        this.membershipId = role.membershipId;
        this.allowMultiple = role.accessScope === "multiple";
        const resp = await PermissionsService.getAllModulePermissionsByMembership(this.membershipId, this.roleId);
        const modules = (resp && resp.data) || [];
        // Normalise permitted to boolean.
        modules.forEach((p) => (p.modules || []).forEach((m) => {
          (m.userrole_membership_permissions || []).forEach((s) => { s.permitted = s.permitted === true || s.permitted === 1; });
        }));
        this.appPermissions = modules;
      } catch (e) {
        this.$notify({ text: (e && e.data && e.data.message) || "Failed to load permissions", type: "error" });
      } finally {
        this.loading = false;
      }
    },
    // Display label for a module row: "Buying Station" reads as "Factory" for Kenya clients,
    // and "Processing" reads as "Processing Batch". Everything else uses the module name.
    moduleDisplayName(item) {
      if (item.id === "coffee/buying-station") return this.isNaccuUser ? "Factory" : item.name;
      if (item.id === "coffee/buying-station/processing") return "Processing Batch";
      return item.name;
    },
    permFor(module, col) {
      return (module.userrole_membership_permissions || []).find((s) =>
        col.names.includes((s.permission_name || "").toLowerCase()));
    },
    isChecked(module, col) {
      const s = this.permFor(module, col);
      return !!(s && s.permitted);
    },
    isAllChecked(module) {
      const present = this.permCols.map((c) => this.permFor(module, c)).filter(Boolean);
      return present.length > 0 && present.every((s) => s.permitted);
    },
    togglePerm(module, col, val) {
      const s = this.permFor(module, col);
      if (!s) return;
      s.permitted = val;
      this.trackChange(s);
    },
    toggleAll(module, val) {
      this.permCols.forEach((c) => {
        const s = this.permFor(module, c);
        if (s) { s.permitted = val; this.trackChange(s); }
      });
    },
    trackChange(s) {
      const existing = _.find(this.changedPerm, { id: s.id });
      if (existing) existing.permitted = s.permitted;
      else this.changedPerm.push({ id: s.id, permitted: s.permitted });
    },
    async savePermissions() {
      if (!this.changedPerm.length) return;
      this.saving = true;
      try {
        const res = await PermissionsService.updateAppPermissions({ modulePermissionUpdates: this.changedPerm });
        if (res && res.success) {
          this.changedPerm = [];
          this.$notify({ title: this.$t("permissions.permissionsUpdated"), type: "success" });
        } else {
          this.$notify({ title: this.$t("permissions.permissionsNotUpdated"), type: "error" });
        }
      } catch (e) {
        this.$notify({ title: this.$t("permissions.permissionsNotUpdated"), type: "error" });
      } finally {
        this.saving = false;
      }
    },
    async saveAccessScope(val) {
      this.savingScope = true;
      try {
        const res = await BuyingStationService.updateStationRoleAccessScope(this.roleId, val);
        if (res && res.success) {
          this.$notify({ text: res.message, type: "success" });
        } else {
          this.allowMultiple = !val; // revert
          this.$notify({ text: (res && res.message) || this.$t("memberDataSection.updateFailed"), type: "error" });
        }
      } catch (e) {
        this.allowMultiple = !val; // revert
        this.$notify({ text: (e && e.data && e.data.message) || this.$t("memberDataSection.updateFailed"), type: "error" });
      } finally {
        this.savingScope = false;
      }
    },
    goBack() {
      // Pop the entry we came in on so we don't stack duplicate Roles/Permissions history
      // (that pileup is what makes the browser Back button feel like a loop). Fall back to a
      // clean push when the page was opened directly (no in-app history to pop).
      if (this.cameFromApp) this.$router.back();
      else this.$router.push({ name: "CoffeeBuyingStationRoles" });
    },
  },
};
</script>

<style lang="scss" scoped>
.perm-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
::v-deep .perm-table .v-data-table-header {
  background-color: $secondary-color;
}
::v-deep .perm-table .v-data-table-header th {
  color: #fff !important;
  text-transform: uppercase;
}
</style>
